import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Map as MapIcon, Trash2 } from "lucide-react";
import { roadmapAdminAccess, roadmapAdminEnable, roadmapAdminDisable } from "@/lib/afmApi";
import { Loading, ErrorState } from "@/components/common";

const errText = (e) => e?.response?.data?.detail || "No se pudo completar";
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";

// Super admin: la Hoja de ruta no viene activada para nadie. Aquí se le da a una cuenta o a un sello.
export default function AdminHojaDeRuta() {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  const [email, setEmail] = useState("");
  const [mid, setMid] = useState("");
  const load = () => { setFailed(false); roadmapAdminAccess().then(setD).catch(() => setFailed(true)); };
  useEffect(load, []);
  if (failed) return <ErrorState onRetry={load} />;
  if (!d) return <Loading />;
  const enabledMgr = new Set(d.access.filter((a) => a.kind === "manager").map((a) => a.manager_id));

  const enable = async (body) => {
    try { await roadmapAdminEnable(body); toast.success("Hoja de ruta activada"); setEmail(""); setMid(""); load(); } catch (e) { toast.error(errText(e)); }
  };
  const disable = async (a) => {
    if (!window.confirm(`¿Quitarle la Hoja de ruta a ${a.label}? Sus hojas no se borran: quedan en solo lectura.`)) return;
    try { await roadmapAdminDisable(a.id); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-roadmap">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#CA0808]">Administración</div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-black">Hoja de ruta · acceso</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">No viene activada para nadie. Actívala para una cuenta (por su correo) o para un sello completo: su dueño y su equipo con Catálogo la editan; sus artistas con acceso ven solo las suyas.</p>
        </div>
        <Link to="/hoja-de-ruta" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"><MapIcon className="h-4 w-4" /> Ver todas las hojas</Link>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-black">Activar para una cuenta</div>
          <div className="flex gap-2">
            <input className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@artista.com" />
            <button onClick={() => enable({ kind: "user", email })} className="shrink-0 rounded-lg bg-black px-4 text-sm font-semibold text-white">Activar</button>
          </div>
        </div>
        <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-black">Activar para un sello</div>
          <div className="flex gap-2">
            <select className={inputCls} value={mid} onChange={(e) => setMid(e.target.value)}>
              <option value="">Elige un sello…</option>
              {d.managers.filter((m) => !enabledMgr.has(m.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button disabled={!mid} onClick={() => enable({ kind: "manager", manager_id: mid })} className="shrink-0 rounded-lg bg-black px-4 text-sm font-semibold text-white disabled:opacity-40">Activar</button>
          </div>
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2 text-left">Quién</th><th className="px-4 py-2 text-left">Tipo</th><th className="px-4 py-2 text-left">Hojas</th><th className="px-4 py-2 text-left">Activó</th><th /></tr></thead>
          <tbody>
            {d.access.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Nadie la tiene todavía.</td></tr>}
            {d.access.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-semibold text-black">{a.label}</td>
                <td className="px-4 py-2 text-slate-600">{a.kind === "manager" ? "Sello" : "Cuenta"}</td>
                <td className="px-4 py-2 text-slate-600">{a.roadmaps}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{a.enabled_by}</td>
                <td className="px-4 py-2 text-right"><button onClick={() => disable(a)} className="p-1 text-red-500" title="Quitar acceso"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
